// PET-655 Part B — Training Module clipboard payload.
//
// Both clipboard flavours route through the refMap so pasted content
// shows ``<phone> (<ordinal>)`` instead of raw Dialpad call IDs (the
// prototype's own module copy had this raw-ID bug — fixed here, not
// copied):
//  - text/html: call IDs become anchors with ABSOLUTE call-detail
//    hrefs, so links survive pasting outside the app shell;
//  - text/plain: call IDs become ``<phone> (<ordinal>) (url)``,
//    consistent with the tips copy payload.
import {
  callIdsToPlainText,
  linkifyCallIdsInHtml,
  type CallRefMap,
} from "../lib/linkifyCallIds";

export function buildModuleCopyPayload(
  html: string,
  refMap: CallRefMap,
  stamp: { formatted: string; id: string } | null,
  agentName: string,
): { html: string; text: string } {
  const title = `Training Module for ${agentName}`;
  const titleHtml = `<h2>${escapeHtml(title)}</h2>`;
  const stampHtml = stamp
    ? `<p><strong>Generated:</strong> ${escapeHtml(stamp.formatted)}<br>` +
      `<strong>Ref TT Report ID:</strong> <code>${escapeHtml(stamp.id)}</code></p>`
    : "";
  const linkedHtml = linkifyCallIdsInHtml(html, refMap, {
    absoluteHrefs: true,
  });
  const bodyText = callIdsToPlainText(htmlToPlainText(html), refMap);
  const stampText = stamp
    ? `Generated: ${stamp.formatted}\nRef TT Report ID: ${stamp.id}\n\n`
    : "";
  return {
    html: `<!doctype html><html><body>${titleHtml}${stampHtml}${linkedHtml}</body></html>`,
    text: `${title}\n\n${stampText}${bodyText}`,
  };
}

function htmlToPlainText(html: string): string {
  // Browser parsing is the most accurate way to flatten the HTML we
  // just rendered. Falls back to a simple regex strip if DOMParser
  // isn't available (SSR-style environments).
  if (typeof window !== "undefined" && typeof DOMParser !== "undefined") {
    const doc = new DOMParser().parseFromString(
      `<!doctype html><body>${html}</body>`,
      "text/html",
    );
    return (doc.body.textContent || "").replace(/\n{3,}/g, "\n\n").trim();
  }
  return html
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}
